import { LocaleLink } from "@/components/locale-link";
import { BlogArchivePage } from "@/components/blog/BlogArchivePage";
import { BlogPostItem } from "@/components/blog/BlogPostItem";
import { BlogPagination } from "@/components/blog/BlogPagination";
import type { BlogPostsPage } from "@/lib/blog.types";
import { useTranslation } from "@/common/providers/i18n-provider";
import { blogDescriptionKey, blogTitleKey } from "@/config/blog";

/**
 * Header copy is addressed by message key, never a resolved string: both the
 * blog and research log must render the reader's locale (w3/m24/t003).
 */
export function BlogListPage({
  page,
  basePath = "/blog",
  titleKey = blogTitleKey,
  descriptionKey = blogDescriptionKey,
  partOf,
}: {
  page: BlogPostsPage;
  /** URL base for this blog instance, without a trailing slash. */
  basePath?: string;
  titleKey?: string;
  descriptionKey?: string;
  /**
   * Optional "Part of …" link above the archive links (research-log listing →
   * Bean Labs hub). Key must live in a namespace the listing already loads.
   */
  partOf?: { to: string; labelKey: string };
}) {
  const { t } = useTranslation();
  const { posts } = page;
  // Only page one promotes a pinned post. Every other entry stays in source order.
  const featured =
    page.page === 1 ? posts.find((post) => post.pinned) : undefined;
  const rest = featured ? posts.filter((post) => post !== featured) : posts;

  return (
    <BlogArchivePage basePath={basePath}>
      <header className="archive-hero">
        <h1>{t(titleKey)}</h1>
        <div>
          <p className="company-lead">{t(descriptionKey)}</p>
          <div className="archive-links">
            {partOf ? (
              <LocaleLink to={partOf.to} className="company-text-link">
                {t(partOf.labelKey)}
              </LocaleLink>
            ) : null}
            <LocaleLink to={`${basePath}/tags`} className="company-text-link">
              {t("blog.browseByTag")}
            </LocaleLink>
            <LocaleLink
              to={`${basePath}/authors`}
              className="company-text-link"
            >
              {t("theme.blog.authorsList.viewAll")}
            </LocaleLink>
          </div>
        </div>
      </header>
      {posts.length > 0 ? (
        <>
          {featured ? (
            <BlogPostItem
              post={featured}
              basePath={basePath}
              featured
              priority
            />
          ) : null}
          <div className="archive-grid">
            {rest.map((post, index) => (
              <BlogPostItem
                key={`${post.date}-${post.slug}`}
                post={post}
                basePath={basePath}
                priority={!featured && index === 0}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="archive-empty">
          <p>{t("blog.noPosts")}</p>
        </div>
      )}
      <BlogPagination page={page} indexPath={basePath} />
    </BlogArchivePage>
  );
}
